import { GlobalDatePicker } from "@/components/ui/GlobalDatePicker";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { useEmployeesOverview } from "@/hooks/useEmployeesOverview";
import { useAuthStore } from "@/store/useAuthStore";
import { useThemeStore, type ThemeMode } from "@/store/useThemeStore";
import { cn } from "@/utils/cn";
import { getBrowserTimezone, getTimezoneLabel } from "@/utils/displayTime";
import { selectOrgApi } from "@/api/auth.api";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import {
  Building2,
  ChevronDown,
  Clock,
  LogOut,
  Monitor,
  Moon,
  Settings,
  Sun,
  User,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

dayjs.extend(utc);
dayjs.extend(timezone);

interface TopNavProps {
  title: string;
}

/* ── Theme toggle button ── */
function ThemeToggle() {
  const { mode, setMode } = useThemeStore();

  const modes: { value: ThemeMode; icon: typeof Sun; label: string }[] = [
    { value: "light", icon: Sun, label: "Light" },
    { value: "dark", icon: Moon, label: "Dark" },
    { value: "system", icon: Monitor, label: "System" },
  ];

  return (
    <div className="flex items-center rounded-lg border border-border bg-surface-overlay p-0.5">
      {modes.map((m) => {
        const Icon = m.icon;
        const active = mode === m.value;
        return (
          <button
            key={m.value}
            onClick={() => setMode(m.value)}
            title={m.label}
            className={cn(
              "rounded-md p-1.5 transition-all",
              active
                ? "bg-surface-raised text-brand-primary shadow-sm"
                : "text-text-muted hover:text-text-secondary",
            )}
          >
            <Icon className="h-3.5 w-3.5" />
          </button>
        );
      })}
    </div>
  );
}

/* ── Organization switcher dropdown ── */
function OrgSwitcher() {
  const { organizations, currentOrgId, setOrg } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!organizations || organizations.length <= 1) return null;

  const currentOrg = organizations.find((o) => o.orgId === currentOrgId);
  const displayName = currentOrg?.orgName || "Select Organization";

  async function handleSwitch(orgId: string, role: string) {
    setSwitching(true);
    try {
      const { token } = await selectOrgApi(orgId);
      setOrg(orgId, role, token);
      setOpen(false);
      window.location.reload();
    } catch {
      // Silently fail — user stays in current org
    } finally {
      setSwitching(false);
    }
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        disabled={switching}
        className="flex items-center gap-2 rounded-lg border border-border bg-surface-overlay px-3 py-1.5 text-sm font-medium text-text-secondary transition-colors hover:border-border-focus hover:text-text-primary disabled:opacity-60"
      >
        <Building2 className="h-3.5 w-3.5 text-text-muted" />
        <span className="max-w-[120px] truncate">{displayName}</span>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 text-text-muted transition-transform",
            open && "rotate-180",
          )}
        />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 w-56 rounded-lg border border-border bg-surface-raised py-1 shadow-card">
          {organizations.map((org) => (
            <button
              key={org.orgId}
              onClick={() => handleSwitch(org.orgId, org.role)}
              className={cn(
                "flex w-full items-center text-left gap-2 px-3 py-2 text-sm transition-colors hover:bg-surface-hover",
                currentOrgId === org.orgId
                  ? "font-medium text-brand-primary"
                  : "text-text-secondary",
              )}
            >
              <Building2 className="h-4 w-4 text-text-muted" />
              <div className="flex-1 truncate">
                <div className="truncate">{org.orgName}</div>
                <div className="text-xs text-text-muted capitalize">
                  {org.role.toLowerCase()}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── User avatar dropdown ── */
function UserMenu() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const { data: employees } = useEmployeesOverview();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const emp = employees
    ? employees.find((e: any) => e.userId === user?.id)
    : null;

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!user) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="rounded-full ring-2 ring-transparent transition-all hover:ring-brand-primary/40 focus:outline-none"
        aria-label="User menu"
      >
        <UserAvatar
          name={user.fullName}
          isCheckedIn={emp?.isCheckedIn}
          isCurrentlyPaused={emp?.isCurrentlyPaused}
          size="sm"
        />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-52 rounded-xl border border-border bg-surface-raised shadow-card">
          {/* User info header */}
          <div className="border-b border-border-subtle px-4 py-3">
            <p className="truncate text-sm font-semibold text-text-primary">
              {user.fullName}
            </p>
            <p className="truncate text-xs text-text-muted">{user.email}</p>
          </div>

          {/* Actions */}
          <div className="py-1">
            <button
              onClick={() => {
                setOpen(false);
                navigate("/settings");
              }}
              className="flex w-full items-center gap-2.5 px-4 py-2 text-sm text-text-secondary transition-colors hover:bg-surface-hover hover:text-text-primary"
            >
              <User className="h-4 w-4 text-text-muted" />
              Edit Profile
            </button>
            <button
              onClick={() => {
                setOpen(false);
                navigate("/settings");
              }}
              className="flex w-full items-center gap-2.5 px-4 py-2 text-sm text-text-secondary transition-colors hover:bg-surface-hover hover:text-text-primary"
            >
              <Settings className="h-4 w-4 text-text-muted" />
              Settings
            </button>
          </div>

          <div className="border-t border-border-subtle py-1">
            <button
              onClick={() => {
                setOpen(false);
                logout();
              }}
              className="flex w-full items-center gap-2.5 px-4 py-2 text-sm text-signal-error transition-colors hover:bg-signal-error/10"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function TopNav({ title }: TopNavProps) {
  const browserTimezone = getBrowserTimezone();
  const [localTime, setLocalTime] = useState(() =>
    dayjs().tz(browserTimezone).format("hh:mm:ss A"),
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setLocalTime(dayjs().tz(browserTimezone).format("hh:mm:ss A"));
    }, 1000);
    return () => clearInterval(interval);
  }, [browserTimezone]);

  return (
    <header className="flex h-14 items-center justify-between border-b border-border bg-surface-card px-6">
      <h1 className="text-lg font-semibold text-text-primary">{title}</h1>

      <div className="flex items-center gap-3">
        {/* Organization switcher (shown when user has 2+ orgs) */}
        <OrgSwitcher />

        {/* Global date picker */}
        <GlobalDatePicker />

        {/* Theme toggle */}
        <ThemeToggle />

        {/* Separator */}
        <div className="h-6 w-px bg-border-subtle" />

        {/* Local clock */}
        <div className="flex items-center gap-1.5 text-sm text-text-secondary">
          <Clock className="h-4 w-4 text-text-muted" />
          <span className="font-mono tabular-nums">{localTime}</span>
          <span className="text-xs text-text-muted" title={browserTimezone}>
            {getTimezoneLabel(browserTimezone)}
          </span>
        </div>

        {/* User avatar + dropdown */}
        <UserMenu />
      </div>
    </header>
  );
}
